/**
 * InformantManagementContent
 * ==========================
 *
 * The shared content component that powers both the Provider and Patient
 * informant management modals.  This component handles two things:
 *
 *   1. **Summary + Detail screens** — rendered inline as list/detail views
 *   2. **Action flow delegation** — each action (Replace, Email Change,
 *      Assignment, Add Informant, Resend Welcome, Split, Swap) is handled
 *      by a dedicated subcomponent that owns its own form state, validation,
 *      and success screen.
 *
 * The parent provides the Radix AlertDialog shell and passes configuration
 * props that control terminology, feature availability, and patient identity.
 *
 * Screen routing is managed via a `currentScreen` state string.  When the
 * user is inside any action flow (screen !== 'summary' / 'detail'), the
 * Firestore onSnapshot sync effect is guarded — this prevents real-time
 * data updates from yanking the user away before they finish an action.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  UserIcon,
  PencilIcon,
  DuplicateIcon,
  MailIcon,
  SwitchHorizontalIcon,
  UserAddIcon,
  TrashIcon,
  ShieldExclamationIcon,
  LockClosedIcon,
  LockOpenIcon,
} from '@heroicons/react/outline';
import useInformantManagement, { getInformantStatus } from '../../hooks/useInformantManagement';
import { formatDateTime } from '../../utils/timestampUtils';
import {
  FORM_LOCK_BEFORE_APPOINTMENT_MESSAGE,
  hasCypAppointmentReportBeenGenerated,
  isFormLockOrgExempt,
} from '../../utils/formLockingUtils';
import { clampPercentage } from '../../utils/formProgress';
import WarningPanel from './WarningPanel';
import ActionRow from './ActionRow';
import InfoTooltip from '../InfoTooltip';

// ── Action flow subcomponents ──
import SplitInformantFlow from './SplitInformantFlow';
import SwapInformantsFlow from './SwapInformantsFlow';
import ReplaceInformantFlow from './ReplaceInformantFlow';
import RemoveInformantFlow from './RemoveInformantFlow';
import ChangeEmailFlow from './ChangeEmailFlow';
import ChangeRelationshipFlow from './ChangeRelationshipFlow';
import AddInformantFlow from './AddInformantFlow';
import ResendWelcomeFlow from './ResendWelcomeFlow';
import AssignQuestionnairesFlow from './AssignQuestionnairesFlow';
import ResetMfaFlow from '../userManagement/ResetMfaFlow';

/**
 * Maps Autism informant form keys to their user-friendly display names.
 * These mirror the canonical form titles from the Python constants
 * (adult_autism_informant/history/) but in third-person for the provider/patient view.
 */
const AUTISM_FORM_DISPLAY_NAMES = {
  yourWellbeing: 'Their Wellbeing',
  yourHealth: 'Their Health',
  yourFamily: 'Their Family',
  yourChildhood: 'Their Childhood',
  yourAdultLife: 'Their Adult Life',
  yourLifestyle: 'Their Lifestyle',
  youAndTheLaw: 'The Law',
};

/**
 * CYP ADHD informant form keys to display names (form titles from
 * python-functions/constants/cyp_adhd/, de-personalised for this view).
 * Merged with the autism map below — the key sets do not overlap.
 */
const CYP_FORM_DISPLAY_NAMES = {
  aboutYouAndConsent: 'About you and the child',
  reasonForReferral: 'Reason for referral',
  theirEarlyLife: 'Their early life',
  theirFamilyAndHomeLife: 'Their family and home life',
  theirLifestyle: 'Their lifestyle',
  theirHistoryWithTheLaw: 'Their history with the law',
  theirFamilyHistory: 'Their family history',
  theirMedicalHistory: 'Their medical history',
  theirMentalHealthHistory: 'Their mental health history',
  educationHistory: 'Education history',
  snapIV26Informant: 'ADHD symptoms scale (SNAP-IV 26)',
};

const MODULAR_FORM_DISPLAY_NAMES = {
  ...AUTISM_FORM_DISPLAY_NAMES,
  ...CYP_FORM_DISPLAY_NAMES,
};

/** Role labels for CYP ADHD informants — never shown as generic "Informant". */
const CYP_ROLE_LABELS = {
  legalGuardian: 'Parent/carer',
  schoolRep: 'School representative',
};

/**
 * Terminology for one CYP informant, derived from their role, in the same
 * shape as the pathway-level terminology prop. The CYP design rule is that
 * the UI never calls these people a generic "Informant" — a teacher is a
 * school representative, a parent is a parent/carer.
 */
function cypRoleTerminology(informant) {
  const label = CYP_ROLE_LABELS[informant?.informantRole] || CYP_ROLE_LABELS.legalGuardian;
  return {
    singular: label,
    plural: `${label}s`,
    lowerSingular: label.toLowerCase(),
    lowerPlural: `${label.toLowerCase()}s`,
  };
}


/**
 * @param {Object}   props
 * @param {string}   props.patientId          – UID of the patient
 * @param {Object}   props.patientDataModel   – Full patient document
 * @param {Function} props.refreshPatientData – Callback to reload patient data
 * @param {Function} props.onClose            – Close the modal
 * @param {string}   props.callerType         – 'provider' | 'patient'
 * @param {Object}   props.terminology        – { singular, plural, lowerSingular, lowerPlural }
 * @param {boolean}  [props.showResendWelcome] – Whether to show the resend welcome action
 * @param {Object}   [props.reportContent] – CYP report documents, keyed by stage.
 *   Used to tell whether the first appointment section has been generated.
 */
export default function InformantManagementContent({
  patientId,
  patientDataModel,
  refreshPatientData,
  onClose,
  callerType = 'patient',
  terminology = {
    singular: 'Informant',
    plural: 'Informants',
    lowerSingular: 'informant',
    lowerPlural: 'informants',
  },
  showResendWelcome = false,
  reportContent = {},
}) {
  // ── Hook for all data and actions ──
  const {
    informants,
    isLoading,
    loadError,
    availableActions,
    isAutismPathway,
    isCypPathway,
    actionLoading,
    actionError,
    actionSuccess,
    clearActionState,
    getProgressWarning,
    splitInformant,
    swapInformants,
    setupNewInformant,
    changeEmail,
    changeRelationship,
    replaceInformant,
    resendWelcomeEmail,
    resetInformantMfa,
    deactivateInformant,
    reassignQuestionnaires,
    toggleInformantFormLock,
    sanitizeNameInput,
    sanitizeEmailInput,
    refreshInformants,
  } = useInformantManagement({
    patientId,
    patientDataModel,
    refreshPatientData,
    callerType,
    terminology,
  });

  // ── Screen routing ──
  // Only two pieces of routing state needed: which screen and which informant.
  const [currentScreen, setCurrentScreen] = useState('summary');
  const [selectedInformant, setSelectedInformant] = useState(null);

  // Context for specific flows — set when the flow opens, read when
  // rendering the corresponding subcomponent.
  const [assignmentTarget, setAssignmentTarget] = useState(null);
  const [addLifeStage, setAddLifeStage] = useState(null);
  // CYP ADHD add flow: which role slot is being filled. Captured when the
  // flow opens (like the split/swap snapshots) so a background refresh can't
  // change the role mid-flow.
  const [addCypRole, setAddCypRole] = useState(null);

  // Snapshot references for Split / Swap flows.  Captured at the time the
  // flow opens so that real-time Firestore listeners (patient-side
  // onSnapshot) don't nullify availableActions mid-flow.
  const [splitInfSnapshot, setSplitInfSnapshot] = useState(null);
  const [swapInfSnapshots, setSwapInfSnapshots] = useState([null, null]);

  // Loading state for the "Finish" button on success screens — shows a
  // spinner while data refreshes after the user dismisses a success screen.
  const [isFinishing, setIsFinishing] = useState(false);

  // Guard against setState after unmount (modal closed during async work).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Smooth height transitions ──
  // Uses a ResizeObserver to measure the inner content's natural height and
  // applies an explicit `height` on an outer wrapper with a CSS transition.
  // This smoothly animates all screen changes: summary ↔ detail, entering
  // and leaving action flows, and internal content resizes (e.g. error
  // panels appearing).  Same pattern as SetUpMFA.js.
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(null);

  // Measure the inner content's natural height via ResizeObserver and apply it
  // to the animated outer wrapper. A small buffer prevents the bottom of action
  // flows (e.g. Back / Confirm buttons) from being clipped by overflow-hidden
  // during the height transition — same scrollHeight approach as SetUpMFA.js.
  const measureContentHeight = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setContentHeight(Math.ceil(el.scrollHeight) + 4);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      measureContentHeight();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureContentHeight]);

  // Re-measure immediately when the screen changes so the animated wrapper
  // doesn't keep a stale height from the previous screen (which can clip
  // shorter action flows like Resend Welcome Email).
  useEffect(() => {
    measureContentHeight();
  }, [currentScreen, measureContentHeight]);

  // ── Navigation helpers ──

  const goToSummary = useCallback(() => {
    setCurrentScreen('summary');
    setSelectedInformant(null);
    clearActionState();
  }, [clearActionState]);

  const goToDetail = useCallback((informant) => {
    setSelectedInformant(informant);
    setCurrentScreen('detail');
    clearActionState();
  }, [clearActionState]);

  /**
   * Consolidated finish handler for data-changing actions.
   * Refreshes both patient data and informant data in parallel, then
   * navigates to the summary screen.  Called by Replace, Email Change,
   * Split, Swap, Assignment, and Add Informant success screens.
   */
  const handleFinish = useCallback(async () => {
    setIsFinishing(true);
    await Promise.all([
      refreshPatientData().catch(() => {}),
      refreshInformants(),
    ]);
    if (!mountedRef.current) return;
    setIsFinishing(false);
    setCurrentScreen('summary');
    setSelectedInformant(null);
    clearActionState();
  }, [refreshPatientData, refreshInformants, clearActionState]);

  /**
   * Finish handler that navigates back to the informant's detail screen
   * instead of the summary.  Used by the Assignment flow where the
   * informant still exists after the action and the user expects to
   * return to their detail view.
   */
  const handleFinishToDetail = useCallback(async () => {
    setIsFinishing(true);
    await Promise.all([
      refreshPatientData().catch(() => {}),
      refreshInformants(),
    ]);
    if (!mountedRef.current) return;
    setIsFinishing(false);
    setCurrentScreen('detail');
    clearActionState();
  }, [refreshPatientData, refreshInformants, clearActionState]);

  // ── Keep selectedInformant in sync with refreshed data ──
  //
  // When informants refresh (e.g. after a "Finish" handler runs, or from
  // the patient-side onSnapshot listener), update the selected informant's
  // data to reflect the latest state.  If the selected informant was
  // removed, navigate back to the summary.
  //
  // GUARD: When the user is inside an action flow (any screen other than
  // 'summary' or 'detail'), skip this sync entirely.  The action flow
  // subcomponent owns its own state and will be unmounted when the user
  // finishes, at which point this effect runs on the clean 'summary' or
  // 'detail' screen with fresh data.
  const selectedIdRef = useRef(null);
  selectedIdRef.current = selectedInformant?.id ?? null;

  useEffect(() => {
    const isInActionFlow = currentScreen !== 'summary' && currentScreen !== 'detail';
    if (isInActionFlow) return;

    const currentId = selectedIdRef.current;
    if (currentId && informants.length > 0) {
      const updated = informants.find(i => i.id === currentId);
      if (updated) {
        setSelectedInformant(updated);
      } else {
        setSelectedInformant(null);
        setCurrentScreen('summary');
      }
    }
  }, [informants, currentScreen]);

  // ── Flow openers ──
  // These set the screen and any context needed by the subcomponent.
  // No form state initialization — that's owned by each subcomponent.

  const openReplaceScreen = useCallback((informant) => {
    setSelectedInformant(informant);
    clearActionState();
    setCurrentScreen('replace');
  }, [clearActionState]);

  const openRemoveScreen = useCallback((informant) => {
    setSelectedInformant(informant);
    clearActionState();
    setCurrentScreen('remove');
  }, [clearActionState]);

  const openEmailChangeScreen = useCallback((informant) => {
    setSelectedInformant(informant);
    clearActionState();
    setCurrentScreen('emailChange');
  }, [clearActionState]);

  const openRelationshipChangeScreen = useCallback((informant) => {
    setSelectedInformant(informant);
    clearActionState();
    setCurrentScreen('relationshipChange');
  }, [clearActionState]);

  const openAssignmentScreen = useCallback((informant, targetLifeStage) => {
    setSelectedInformant(informant);
    setAssignmentTarget(targetLifeStage);
    clearActionState();
    setCurrentScreen('assignment');
  }, [clearActionState]);

  const openAddInformantScreen = useCallback((lifeStage, cypRole = null) => {
    setAddLifeStage(lifeStage);
    setAddCypRole(cypRole);
    clearActionState();
    setCurrentScreen('addInformant');
  }, [clearActionState]);

  const openResetMfaScreen = useCallback((informant) => {
    setSelectedInformant(informant);
    clearActionState();
    setCurrentScreen('resetMfa');
  }, [clearActionState]);

  const openResendWelcomeScreen = useCallback((informant) => {
    setSelectedInformant(informant);
    clearActionState();
    setCurrentScreen('resendWelcome');
  }, [clearActionState]);

  const openSplitScreen = useCallback(() => {
    const inf = informants.find(i => i.id === availableActions.splitInformantId);
    if (!inf) return;
    setSplitInfSnapshot(inf);
    clearActionState();
    setCurrentScreen('split');
  }, [clearActionState, informants, availableActions.splitInformantId]);

  const openSwapScreen = useCallback(() => {
    const [idA, idB] = availableActions.swapInformantIds || [];
    const a = informants.find(i => i.id === idA);
    const b = informants.find(i => i.id === idB);
    if (!a || !b) return;
    setSwapInfSnapshots([a, b]);
    clearActionState();
    setCurrentScreen('swap');
  }, [clearActionState, informants, availableActions.swapInformantIds]);

  // ────────────────────────────────────────────────────────────────────
  // SCREEN CONTENT
  // ────────────────────────────────────────────────────────────────────
  // All screens compute into `content` instead of returning early, so
  // we can wrap everything in a single animated-height container.

  const isInActionFlow = currentScreen !== 'summary' && currentScreen !== 'detail';

  let content;

  if (isLoading && !isInActionFlow && informants.length === 0) {
    content = (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600" />
        <span className="ml-3 text-sm text-gray-500">
          Loading {terminology.lowerPlural}…
        </span>
      </div>
    );
  } else if (loadError && !isInActionFlow && informants.length === 0) {
    content = (
      <div className="py-8 px-4 text-center">
        <WarningPanel variant="error">{loadError}</WarningPanel>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Close
        </button>
      </div>
    );

  // ── SUMMARY SCREEN ──
  } else if (currentScreen === 'summary') {
    const coveredStages = new Set(informants.flatMap(inf => inf.lifeStages || []));
    const skipReasons = patientDataModel?.informantSkipReasons || {};

    content = (
      <div className="space-y-3">
        {informants.length > 0 ? (
          <div className="space-y-2">
            {informants.map(inf => {
              const status = getInformantStatus(inf);
              const stages = inf.lifeStages || [];
              // CYP informants are labelled by role — never as a generic
              // "Informant" (design rule for the CYP pathway).
              const stageLabel = isCypPathway
                ? (CYP_ROLE_LABELS[inf.informantRole] || CYP_ROLE_LABELS.legalGuardian)
                : isAutismPathway
                  ? 'Developmental history informant'
                  : stages.length > 0
                    ? `${stages.join(' & ')} ${terminology.singular}`
                    : terminology.singular;
              return (
                <div key={inf.id}>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {stageLabel}
                  </p>
                  <button
                    type="button"
                    onClick={() => goToDetail(inf)}
                    className="w-full flex items-center gap-3 p-3 text-left border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <div
                      className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center shrink-0"
                      aria-hidden="true"
                    >
                      <UserIcon className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-gray-800 truncate">
                        {inf.firstName} {inf.lastName}
                      </span>
                      <span className="block text-xs text-gray-600 truncate">
                        {inf.email}
                      </span>
                      <span className="block text-xs text-gray-500 truncate">
                        {inf.relationship}
                      </span>
                      <span className={`block text-xs mt-0.5 ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <span
                      className="text-gray-400 text-xs shrink-0"
                      aria-hidden="true"
                    >
                      View →
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <UserIcon
              className="w-10 h-10 mx-auto text-gray-300 mb-2"
              aria-hidden="true"
            />
            <p className="text-xs text-gray-500">
              No {terminology.lowerPlural} assigned yet.
            </p>
          </div>
        )}

        {/* ADHD patients skip by life stage (adulthood / childhood). Autism
            patients skip the single developmental-history informant slot and
            store the reason under informantSkipReasons.informant1. Only show
            the Autism card while no informants are assigned — once someone is
            added later, the earlier skip is no longer the active state. */}
        {callerType === 'provider' && isAutismPathway && informants.length === 0 && skipReasons.informant1 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-sm font-semibold text-amber-800">
              Developmental history {terminology.singular} - Skipped
            </p>
            <p className="text-xs text-amber-700 mt-1">
              <span className="font-medium">Reason given:</span>{' '}
              <em>"{skipReasons.informant1}"</em>
            </p>
          </div>
        )}

        {callerType === 'provider' && !isAutismPathway && !isCypPathway && ['Adulthood', 'Childhood'].map(stage => {
          const reasonKey = stage.toLowerCase();
          const reason = skipReasons[reasonKey];
          if (coveredStages.has(stage) || !reason) return null;
          return (
            <div
              key={stage}
              className="bg-amber-50 border border-amber-200 rounded-md p-3"
            >
              <p className="text-sm font-semibold text-amber-800">
                {stage} {terminology.singular} - Skipped
              </p>
              <p className="text-xs text-amber-700 mt-1">
                <span className="font-medium">Reason given:</span>{' '}
                <em>&ldquo;{reason}&rdquo;</em>
              </p>
            </div>
          );
        })}

        {/* CYP ADHD: the guardian can skip nominating a school rep. The skip
            reason lives on the guardian's own informant profile (not on the
            patient's informantSkipReasons), so we read it from the guardian
            informant object. Only shown when no school rep is assigned and
            the guardian explicitly recorded a skip. */}
        {callerType === 'provider' && isCypPathway && (() => {
          const hasSchoolRep = informants.some(inf => inf.informantRole === 'schoolRep');
          if (hasSchoolRep) return null;
          const guardian = informants.find(inf => inf.informantRole === 'legalGuardian');
          const nomination = guardian?.schoolRepNomination;
          if (nomination?.status !== 'skipped' || !nomination.reason) return null;
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm font-semibold text-amber-800">
                School representative — Skipped
              </p>
              <p className="text-xs text-amber-700 mt-1">
                <span className="font-medium">Reason given:</span>{' '}
                <em>&ldquo;{nomination.reason}&rdquo;</em>
              </p>
            </div>
          );
        })()}

        {/* Previously removed informants — the backend archives each
            removed informant into `deactivated_informants` on the patient
            document with who removed them, when, and why. Reading it here
            is the same pattern as informantSkipReasons: zero extra Firestore
            reads, the data flows via the existing onSnapshot. Only shown to
            providers — patients/informants should not see removal audit. */}
        {callerType === 'provider' && (() => {
          const deactivated = patientDataModel?.deactivated_informants || {};
          const entries = Object.entries(deactivated);
          if (entries.length === 0) return null;
          return (
            <div className="space-y-2 pt-1">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Previously removed
              </h4>
              {entries.map(([id, entry]) => {
                const name = entry.informantName || [entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'Unknown';
                const reason = entry.deactivationReason;
                const removedBy = entry.deactivatedByName;
                const removedAt = entry.deactivatedAt;
                // CYP informants: show the role label if available.
                const roleLabel = isCypPathway
                  ? (CYP_ROLE_LABELS[entry.informantRole] || null)
                  : null;
                return (
                  <div
                    key={id}
                    className="flex items-start gap-3 p-3 border border-gray-200 rounded-md bg-gray-50 opacity-70"
                  >
                    <div
                      className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5"
                      aria-hidden="true"
                    >
                      <TrashIcon className="w-4 h-4 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0 text-xs">
                      <p className="text-sm font-semibold text-gray-600 truncate">{name}</p>
                      {roleLabel && (
                        <p className="text-gray-500">{roleLabel}</p>
                      )}
                      {reason && reason !== 'Manual removal' && (
                        <p className="text-gray-500 mt-0.5">
                          <span className="font-medium">Reason:</span> {reason}
                        </p>
                      )}
                      <p className="text-gray-400 mt-0.5">
                        Removed{removedBy ? ` by ${removedBy}` : ''}
                        {removedAt ? ` on ${formatDateTime(removedAt)}` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div className="space-y-2 pt-1">
          <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
            Actions
          </h4>

          {availableActions.canSwap && !isAutismPathway && (() => {
            const [idA, idB] = availableActions.swapInformantIds;
            const infA = informants.find(i => i.id === idA);
            const infB = informants.find(i => i.id === idB);
            return (
              <ActionRow
                icon={<SwitchHorizontalIcon className="w-4 h-4" />}
                label={`Switch ${terminology.singular.toLowerCase()} questionnaires`}
                description={`Swap which questionnaire ${infA?.firstName} and ${infB?.firstName} answer`}
                onClick={openSwapScreen}
                variant="primary"
              />
            );
          })()}

          {availableActions.canSplit && !isAutismPathway && (() => {
            const splitInf = informants.find(
              i => i.id === availableActions.splitInformantId
            );
            return (
              <ActionRow
                icon={<UserAddIcon className="w-4 h-4" />}
                label={`Add a second ${terminology.lowerSingular}`}
                description={`${splitInf?.firstName} currently answers both questionnaires`}
                onClick={openSplitScreen}
                variant="primary"
              />
            );
          })()}

          {isAutismPathway && availableActions.canAdd && informants.length === 0 && (
            <ActionRow
              icon={<UserAddIcon className="w-4 h-4" />}
              label={`Add ${terminology.singular}`}
              description={callerType === 'patient'
                ? 'Add someone who knows you well to answer questions about your life to support your assessment.'
                : "Add someone who knows the patient's developmental history"}
              onClick={() => openAddInformantScreen(null)}
            />
          )}

          {isAutismPathway && availableActions.canAdd && informants.length > 0 && (
            <button
              type="button"
              onClick={() => openAddInformantScreen(null)}
              className="w-full text-left px-3 py-2 rounded-md border border-dashed border-gray-300 hover:border-violet-300 hover:bg-violet-50 transition-colors"
            >
              <span className="text-sm text-gray-600 font-medium">
                Add a second {terminology.lowerSingular}
              </span>
              <span className="block text-xs text-gray-400 mt-0.5">
                {callerType === 'patient'
                  ? 'You can optionally add another person who knows you well'
                  : 'You can optionally add another person who knows the patient well'}
              </span>
            </button>
          )}

          {/* CYP ADHD: the only add action is filling whichever role slot is
              empty (exactly one parent/carer + one school representative). */}
          {isCypPathway && availableActions.canAdd && availableActions.addRole && (
            <ActionRow
              icon={<UserAddIcon className="w-4 h-4" />}
              label={availableActions.addRole === 'schoolRep'
                ? 'Add school representative'
                : 'Add parent/carer'}
              description={availableActions.addRole === 'schoolRep'
                ? "Add someone from the child's school or college - like their SENCO or class teacher"
                : "Add the child's responsible parent or carer"}
              onClick={() => openAddInformantScreen(null, availableActions.addRole)}
            />
          )}

          {!isAutismPathway && !isCypPathway && !coveredStages.has('Adulthood') && (
            <ActionRow
              icon={<UserAddIcon className="w-4 h-4" />}
              label={`Add Adulthood ${terminology.singular}`}
              description={callerType === 'patient'
                ? 'Add someone who knows you well in adulthood'
                : 'Add someone who knows the patient well in adulthood'}
              onClick={() => openAddInformantScreen('Adulthood')}
            />
          )}
          {!isAutismPathway && !isCypPathway && !coveredStages.has('Childhood') && (
            <ActionRow
              icon={<UserAddIcon className="w-4 h-4" />}
              label={`Add Childhood ${terminology.singular}`}
              description={callerType === 'patient'
                ? 'Add someone who knows you well in childhood'
                : 'Add someone who knows the patient well in childhood'}
              onClick={() => openAddInformantScreen('Childhood')}
            />
          )}
        </div>
      </div>
    );

  // ── DETAIL SCREEN ──
  } else if (currentScreen === 'detail' && selectedInformant) {
    const status = getInformantStatus(selectedInformant);
    const stages = selectedInformant.lifeStages || [];
    const canAssignOtherLifeStage = stages.length === 1;
    const otherLifeStage = canAssignOtherLifeStage
      ? stages[0] === 'Adulthood' ? 'Childhood' : 'Adulthood'
      : null;
    // CYP informants are addressed by role, never as a generic "informant".
    const detailTerminology = isCypPathway
      ? cypRoleTerminology(selectedInformant)
      : terminology;
    const canChangeEmail = !selectedInformant.hasStarted;
    const emailChangeDisabledReason = !canChangeEmail
      ? `This ${detailTerminology.lowerSingular}'s email cannot be changed after they have started questionnaires. Use 'Replace' instead.`
      : null;
    const canResendWelcome = selectedInformant.hasSetOwnPassword === false;
    const resendDisabledReason = !canResendWelcome
      ? `A welcome email can only be resent before the ${detailTerminology.lowerSingular} has signed in and set up their password.`
      : null;

    // MFA reset is available for informants who have an Auth account.
    // Auth is the source of truth for MFA status (not the Firestore
    // isMFASetUp flag, which can drift). The backend checks Auth
    // directly and returns alreadyReset if no factors are found.
    const canResetMfa = selectedInformant.hasAuthAccount !== false;
    const resetMfaDisabledReason = !canResetMfa
      ? `This ${detailTerminology.lowerSingular} does not have a login account.`
      : null;

    // Human-readable label for the MFA method Firestore has recorded.
    const mfaRecordedLabel = selectedInformant.mfaMethod === 'totp'
      ? 'Recorded method: Authenticator app'
      : selectedInformant.mfaMethod === 'phone'
        ? 'Recorded method: SMS'
        : selectedInformant.isMFASetUp === true
          ? 'Recorded method: Set up (type not recorded)'
          : 'No method recorded';

    content = (
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center shrink-0"
            aria-hidden="true"
          >
            <UserIcon className="w-5 h-5 text-violet-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">
              {selectedInformant.firstName} {selectedInformant.lastName}
            </h3>
            {isCypPathway && (
              <p className="text-xs font-medium text-violet-700">
                {CYP_ROLE_LABELS[selectedInformant.informantRole] || CYP_ROLE_LABELS.legalGuardian}
                {selectedInformant.schoolName ? ` - ${selectedInformant.schoolName}` : ''}
              </p>
            )}
            <p className="text-xs text-gray-600 break-all">
              {selectedInformant.email}
            </p>
            <p className="text-xs text-gray-500">
              {selectedInformant.relationship}
            </p>
            <p className={`text-xs mt-0.5 ${status.color}`}>
              {status.label}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
            Questionnaire Progress
          </h4>
          {/* Scroll when many forms are assigned so Actions stay visible without scrolling the modal */}
          <div className="space-y-2 max-h-52 overflow-y-auto pr-0.5">
          {(isAutismPathway || selectedInformant.isAutismInformant) ? (
            Object.entries(selectedInformant.formProgress || {}).map(([formKey, progress]) => {
              const pct = clampPercentage(progress.percentage || 0);
              return (
                <div
                  key={formKey}
                  className="bg-white rounded-md p-2 border border-gray-200"
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-gray-700">
                      {MODULAR_FORM_DISPLAY_NAMES[formKey] || formKey.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className="text-xs font-medium text-gray-600">
                      {pct}%
                    </span>
                  </div>
                  <div
                    className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-1"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className={`h-full transition-all ${
                        pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-violet-600' : 'bg-gray-300'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  {progress.completedAt && (
                    <p className="text-xs text-green-700">
                      Completed {formatDateTime(progress.completedAt)}
                    </p>
                  )}
                  {!progress.completedAt && progress.lastUpdated && pct > 0 && (
                    <p className="text-xs text-gray-500">
                      Last updated {formatDateTime(progress.lastUpdated)}
                    </p>
                  )}
                </div>
              );
            })
          ) : (
            stages.map(stage => {
            const progress = selectedInformant.formProgress?.[stage] || {};
            const pct = clampPercentage(progress.percentage || 0);
            return (
              <div
                key={stage}
                className="bg-white rounded-md p-2 border border-gray-200"
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium text-gray-700">
                    {stage === 'Adulthood'
                      ? 'Their Symptoms in Adulthood'
                      : 'Their Symptoms in Childhood'}
                  </span>
                  <span className="text-xs font-medium text-gray-600">
                    {pct}%
                  </span>
                </div>
                <div
                  className="h-1.5 bg-gray-200 rounded-full overflow-hidden mb-1"
                  role="progressbar"
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${stage} questionnaire progress: ${pct}%`}
                >
                  <div
                    className={`h-full transition-all ${
                      pct === 100
                        ? 'bg-green-500'
                        : pct > 0
                          ? 'bg-violet-600'
                          : 'bg-gray-300'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {progress.completedAt && (
                  <p className="text-xs text-green-700">
                    Completed {formatDateTime(progress.completedAt)}
                  </p>
                )}
                {!progress.completedAt && progress.lastUpdated && pct > 0 && (
                  <p className="text-xs text-gray-500">
                    Last updated {formatDateTime(progress.lastUpdated)}
                  </p>
                )}
              </div>
            );
          })
          )}
          </div>
        </div>

        <div className="space-y-2 pt-1">
          <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
            Actions
          </h4>

          {!isAutismPathway && canAssignOtherLifeStage && (
            <ActionRow
              icon={<DuplicateIcon className="w-4 h-4" />}
              label={`Assign ${otherLifeStage?.toLowerCase()} questionnaire`}
              description={`Also assign the ${otherLifeStage?.toLowerCase()} questionnaire to this ${terminology.lowerSingular}`}
              onClick={() =>
                openAssignmentScreen(selectedInformant, otherLifeStage)
              }
            />
          )}

          <ActionRow
            icon={<PencilIcon className="w-4 h-4" />}
            label="Change email address"
            description="Update the email used to log in and receive communications"
            onClick={canChangeEmail ? () => openEmailChangeScreen(selectedInformant) : undefined}
            disabled={!canChangeEmail}
            trailing={emailChangeDisabledReason ? (
              <InfoTooltip content={emailChangeDisabledReason} side="left" maxWidth="220px" />
            ) : undefined}
          />

          {/* CYP ADHD guardian: the parent/carer's relationship to the child
              is captured via dedicated questions on the referral forms, so the
              "change relationship" action is redundant. School reps keep it
              because their relationship (e.g. Class teacher, SENCO) is entered
              during setup and may need correcting. Non-CYP pathways always
              show it. */}
          {!(isCypPathway && selectedInformant?.informantRole === 'legalGuardian') && (
            <ActionRow
              icon={<PencilIcon className="w-4 h-4" />}
              label="Change relationship"
              description={callerType === 'patient'
                ? `Update your relationship to this ${detailTerminology.lowerSingular}`
                : isCypPathway
                  ? `Update this ${detailTerminology.lowerSingular}'s relationship to the child`
                  : `Update this ${detailTerminology.lowerSingular}'s relationship to the patient`}
              onClick={() => openRelationshipChangeScreen(selectedInformant)}
            />
          )}

          {/* CYP ADHD per-informant form lock toggle (provider-only).
              Same rule as Adult ADHD's "Unlock questionnaire forms": the
              action does nothing useful until the first CI or SSI section
              has been generated, because that is when forms lock. Before
              that the row stays visible but greyed out, with a tooltip,
              so a clinician does not lock forms before the appointment.
              If forms were already unlocked, re-lock stays available so
              that override can be cleared. Orgs that never lock forms
              do not see the row at all, matching Adult ADHD. */}
          {isCypPathway && showResendWelcome && !isFormLockOrgExempt(patientDataModel?.organisation) && (() => {
            const appointmentStarted = hasCypAppointmentReportBeenGenerated(reportContent);
            const alreadyUnlocked = selectedInformant.formsUnlocked === true;
            const formLockUnavailable = !appointmentStarted && !alreadyUnlocked;
            return (
              <ActionRow
                icon={alreadyUnlocked
                  ? <LockClosedIcon className="w-4 h-4" />
                  : <LockOpenIcon className="w-4 h-4" />}
                label={alreadyUnlocked
                  ? 'Re-lock questionnaire forms'
                  : 'Unlock questionnaire forms'}
                description={alreadyUnlocked
                  ? `${selectedInformant.firstName}'s forms are currently unlocked${
                      selectedInformant.formsUnlockedByName ? ` by ${selectedInformant.formsUnlockedByName}` : ''
                    }. Re-lock to prevent further edits.`
                  : `Temporarily unlock this ${detailTerminology.lowerSingular}'s questionnaire forms so they can make changes.`}
                onClick={formLockUnavailable ? undefined : async () => {
                  const newUnlockState = !selectedInformant.formsUnlocked;
                  try {
                    await toggleInformantFormLock(selectedInformant.id, newUnlockState);
                    await refreshInformants();
                    refreshPatientData().catch(() => {});
                  } catch {
                    // Error is surfaced via actionError from the hook.
                  }
                }}
                disabled={formLockUnavailable || actionLoading}
                trailing={formLockUnavailable ? (
                  <InfoTooltip
                    content={FORM_LOCK_BEFORE_APPOINTMENT_MESSAGE}
                    side="left"
                    maxWidth="280px"
                  />
                ) : undefined}
              />
            );
          })()}

          <ActionRow
            icon={<UserAddIcon className="w-4 h-4" />}
            label={`Replace with new ${detailTerminology.lowerSingular}`}
            description={`Remove this ${detailTerminology.lowerSingular} and assign a new person`}
            onClick={() => openReplaceScreen(selectedInformant)}
            variant="warning"
          />

          {/* CYP ADHD guardian: removing the sole parent/carer is not offered
              because the "Replace" action above already covers the case where
              a different person needs to take over. Removing without replacing
              would leave the child with no guardian account at all. School
              reps and all non-CYP informants keep the standalone remove. */}
          {!(isCypPathway && selectedInformant?.informantRole === 'legalGuardian') && (
            <ActionRow
              icon={<TrashIcon className="w-4 h-4" />}
              label={`Remove ${detailTerminology.lowerSingular}`}
              description={`Permanently remove this ${detailTerminology.lowerSingular} and revoke their access`}
              onClick={() => openRemoveScreen(selectedInformant)}
              variant="warning"
            />
          )}

          {showResendWelcome && (
            <ActionRow
              icon={<MailIcon className="w-4 h-4" />}
              label="Resend welcome email"
              description="Send a new welcome email with login instructions"
              onClick={canResendWelcome ? () => openResendWelcomeScreen(selectedInformant) : undefined}
              disabled={!canResendWelcome}
              trailing={resendDisabledReason ? (
                <InfoTooltip content={resendDisabledReason} side="left" maxWidth="220px" />
              ) : undefined}
            />
          )}

          {/* Reset MFA — provider-only, same gate as Resend Welcome.
              Disabled with tooltip only when the informant has no Auth
              account (CYP school contacts). Auth is the source of truth
              for MFA status; the backend checks directly. */}
          {showResendWelcome && (
            <ActionRow
              icon={<ShieldExclamationIcon className="w-4 h-4" />}
              label="Reset two-factor authentication"
              description={canResetMfa ? mfaRecordedLabel : 'No login account'}
              onClick={canResetMfa ? () => openResetMfaScreen(selectedInformant) : undefined}
              disabled={!canResetMfa}
              trailing={resetMfaDisabledReason ? (
                <InfoTooltip content={resetMfaDisabledReason} side="left" maxWidth="220px" />
              ) : undefined}
            />
          )}
        </div>

        <button
          type="button"
          onClick={goToSummary}
          className="text-sm text-gray-500 hover:text-gray-700 py-2"
        >
          ← Back to summary
        </button>
      </div>
    );

  // ── ACTION FLOWS ──
  } else if (currentScreen === 'split') {
    if (!splitInfSnapshot) {
      content = (
        <div className="py-8">
          <p className="text-sm text-gray-500">
            This action is no longer available.
          </p>
          <button
            type="button"
            onClick={goToSummary}
            className="mt-2 text-sm text-gray-500 hover:text-gray-700"
          >
            ← Back to summary
          </button>
        </div>
      );
    } else {
      content = (
        <SplitInformantFlow
          existingInformant={splitInfSnapshot}
          allInformants={informants}
          onSplit={splitInformant}
          onBack={goToSummary}
          actionLoading={actionLoading}
          actionError={actionError}
          actionSuccess={actionSuccess}
          clearActionState={clearActionState}
          getProgressWarning={getProgressWarning}
          onFinish={handleFinish}
          isFinishing={isFinishing}
          terminology={terminology}
          sanitizeNameInput={sanitizeNameInput}
          sanitizeEmailInput={sanitizeEmailInput}
        />
      );
    }
  } else if (currentScreen === 'swap') {
    const [infA, infB] = swapInfSnapshots;
    if (!infA || !infB) {
      content = (
        <div className="py-8">
          <p className="text-sm text-gray-500">
            This action is no longer available.
          </p>
          <button
            type="button"
            onClick={goToSummary}
            className="mt-2 text-sm text-gray-500 hover:text-gray-700"
          >
            ← Back to summary
          </button>
        </div>
      );
    } else {
      content = (
        <SwapInformantsFlow
          informantA={infA}
          informantB={infB}
          onSwap={swapInformants}
          onBack={goToSummary}
          actionLoading={actionLoading}
          actionError={actionError}
          clearActionState={clearActionState}
          getProgressWarning={getProgressWarning}
          onFinish={handleFinish}
          isFinishing={isFinishing}
          terminology={terminology}
        />
      );
    }
  } else if (currentScreen === 'replace' && selectedInformant) {
    content = (
      <ReplaceInformantFlow
        informant={selectedInformant}
        allInformants={informants}
        onReplace={replaceInformant}
        onBack={() => goToDetail(selectedInformant)}
        actionLoading={actionLoading}
        actionError={actionError}
        clearActionState={clearActionState}
        onFinish={handleFinish}
        isFinishing={isFinishing}
        terminology={isCypPathway ? cypRoleTerminology(selectedInformant) : terminology}
        sanitizeNameInput={sanitizeNameInput}
        sanitizeEmailInput={sanitizeEmailInput}
        callerType={callerType}
        isAutismPathway={isAutismPathway}
      />
    );
  } else if (currentScreen === 'remove' && selectedInformant) {
    content = (
      <RemoveInformantFlow
        informant={selectedInformant}
        onRemove={deactivateInformant}
        onBack={() => goToDetail(selectedInformant)}
        actionLoading={actionLoading}
        actionError={actionError}
        clearActionState={clearActionState}
        onFinish={handleFinish}
        isFinishing={isFinishing}
        terminology={isCypPathway ? cypRoleTerminology(selectedInformant) : terminology}
        callerType={callerType}
        isAutismPathway={isAutismPathway}
      />
    );
  } else if (currentScreen === 'emailChange' && selectedInformant) {
    content = (
      <ChangeEmailFlow
        informant={selectedInformant}
        allInformants={informants}
        onChangeEmail={changeEmail}
        onBack={() => goToDetail(selectedInformant)}
        actionLoading={actionLoading}
        actionError={actionError}
        clearActionState={clearActionState}
        onFinish={handleFinish}
        isFinishing={isFinishing}
        terminology={isCypPathway ? cypRoleTerminology(selectedInformant) : terminology}
        sanitizeEmailInput={sanitizeEmailInput}
      />
    );
  } else if (currentScreen === 'relationshipChange' && selectedInformant) {
    content = (
      <ChangeRelationshipFlow
        informant={selectedInformant}
        onChangeRelationship={changeRelationship}
        onBack={() => goToDetail(selectedInformant)}
        actionLoading={actionLoading}
        actionError={actionError}
        clearActionState={clearActionState}
        onFinish={handleFinishToDetail}
        isFinishing={isFinishing}
        terminology={isCypPathway ? cypRoleTerminology(selectedInformant) : terminology}
        sanitizeNameInput={sanitizeNameInput}
        callerType={callerType}
      />
    );
  } else if (currentScreen === 'assignment' && selectedInformant) {
    content = (
      <AssignQuestionnairesFlow
        informant={selectedInformant}
        allInformants={informants}
        targetLifeStage={assignmentTarget}
        onAssign={reassignQuestionnaires}
        onBack={() => goToDetail(selectedInformant)}
        actionLoading={actionLoading}
        actionError={actionError}
        clearActionState={clearActionState}
        onFinish={handleFinishToDetail}
        isFinishing={isFinishing}
        terminology={terminology}
      />
    );
  } else if (currentScreen === 'addInformant') {
    content = (
      <AddInformantFlow
        lifeStage={addLifeStage}
        cypRole={isCypPathway ? addCypRole : null}
        allInformants={informants}
        onSetup={setupNewInformant}
        onReuseExisting={reassignQuestionnaires}
        onBack={goToSummary}
        actionLoading={actionLoading}
        actionError={actionError}
        clearActionState={clearActionState}
        onFinish={handleFinish}
        isFinishing={isFinishing}
        terminology={terminology}
        sanitizeNameInput={sanitizeNameInput}
        sanitizeEmailInput={sanitizeEmailInput}
        organisation={patientDataModel?.organisation || ''}
        isAutismPathway={isAutismPathway}
        callerType={callerType}
      />
    );
  } else if (currentScreen === 'resetMfa' && selectedInformant) {
    content = (
      <ResetMfaFlow
        targetUser={selectedInformant}
        targetUserType={selectedInformant.isAutismInformant ? 'informant' : 'patient'}
        onReset={() => resetInformantMfa(selectedInformant.id)}
        onBack={() => goToDetail(selectedInformant)}
        onFinish={() => goToDetail(selectedInformant)}
        actionLoading={actionLoading}
        actionError={actionError}
        clearActionState={clearActionState}
      />
    );
  } else if (currentScreen === 'resendWelcome' && selectedInformant) {
    content = (
      <ResendWelcomeFlow
        informant={selectedInformant}
        patientId={patientId}
        onResend={resendWelcomeEmail}
        onBack={() => goToDetail(selectedInformant)}
        actionLoading={actionLoading}
        actionError={actionError}
        clearActionState={clearActionState}
        onFinish={() => goToDetail(selectedInformant)}
        terminology={isCypPathway ? cypRoleTerminology(selectedInformant) : terminology}
      />
    );
  } else {
    content = (
      <div className="py-8">
        <p className="text-sm text-gray-500">Something went wrong.</p>
        <button
          type="button"
          onClick={goToSummary}
          className="mt-2 text-sm text-gray-500 hover:text-gray-700"
        >
          ← Back to summary
        </button>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // ANIMATED WRAPPER
  // ────────────────────────────────────────────────────────────────────
  // The outer div has an explicit height set via ResizeObserver with a
  // CSS transition, so every screen change (summary → detail, detail →
  // action flow, etc.) smoothly animates the container height.

  return (
    <div
      className="overflow-hidden transition-[height] duration-300 ease-out"
      style={{ height: contentHeight != null ? contentHeight : 'auto' }}
    >
      <div ref={contentRef} className="pb-1">
        <div key={currentScreen} className="animate-fadeIn">
          {content}
        </div>
      </div>
    </div>
  );
}
